import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { openFeedback, useGameNavCount } from "../../lib/feedback";
import { type ThemeColors } from "../../lib/theme";
import { type NavButton } from "./NavbarButtons";

/**
 * Mobile navigation component that provides tab-based navigation at the bottom of the screen.
 *
 * ## Styling Nav Content
 * Use `theme.sectionBg` and `theme.sectionBorder` to style the nav sections.
 * The component does not automatically apply `sectionBg` or `sectionBorder` to children.
 * This gives you full control to style different parts of the nav content separately.
 * For example, if you want a toggle button at the top visually separated from the main
 * content below.
 *
 * @example
 * ```tsx
 * <MobileNav
 *   currentTab={mobileTab}
 *   onTabChange={setMobileTab}
 *   isDark={isDark}
 *   theme={theme}
 *   buttons={navbarButtons}
 * >
 *   <div className="title-style"> Title </div>
 *   <div className={`${theme.sectionBg} ${theme.sectionBorder} p-6`}>
 *     {/* tab content *\/}
 *   </div>
 * </MobileNav>
 * ```
 */

interface MobileNavProps {
  children?: ReactNode;
  currentTab: NavButton;
  onTabChange: (tab: NavButton) => void;
  isDark: boolean;
  theme: ThemeColors;
  buttons: readonly NavButton[];
  className?: string;
}

export function MobileNav({
  currentTab,
  onTabChange,
  isDark,
  theme,
  buttons,
  className = "",
  children,
}: MobileNavProps) {
  const { t } = useTranslation();

  useEffect(() => {
    useGameNavCount.setState((s) => ({ count: s.count + 1 }));
    return () => useGameNavCount.setState((s) => ({ count: s.count - 1 }));
  }, []);

  return (
    <div className={`min-h-0 flex flex-col ${className}`}>
      <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
      <nav
        className={`md:hidden shrink-0 flex border-t ${isDark ? "border-zinc-900 bg-black" : "border-gray-200 bg-white"} h-16`}
      >
        {buttons.map((button) => {
          const isActive = currentTab.id === button.id;
          return (
            <button
              key={button.id}
              onClick={() => onTabChange(button)}
              className={`flex-1 flex flex-col items-center justify-center relative transition-colors duration-200 cursor-pointer ${
                isActive
                  ? isDark
                    ? "text-white"
                    : "text-black"
                  : theme.textMuted
              }`}
            >
              {/* Active indicator pill */}
              {isActive && (
                <span
                  className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full ${
                    isDark ? "bg-white" : "bg-black"
                  }`}
                />
              )}

              <span
                className={`transition-transform duration-200 ${isActive ? "scale-110" : ""}`}
              >
                {button.icon}
              </span>
              <span className="text-xs mt-1.5 font-medium tracking-wide uppercase">
                {t(button.label)}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          data-testid="nav-feedback-btn"
          onClick={() => openFeedback()}
          className={`flex-1 flex flex-col items-center justify-center transition-colors duration-200 cursor-pointer ${theme.textMuted}`}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
          <span className="text-xs mt-1.5 font-medium tracking-wide uppercase">
            {t("components.navbar.feedback")}
          </span>
        </button>
      </nav>
    </div>
  );
}
